import type { FormInstance } from '@arco-design/web-react';

import { type VendorDeviceSelection, formatVendorDeviceLabel } from '@/delivery-type-plugin/components';
import { formatCapacityForDisplay } from '@/delivery-type-plugin/components/quantityUnit';
import { buildOptionFromRaw, syncOptionMetaFromRaw } from '@/delivery-type-plugin/platform/option-meta';

import { BARE_METAL_NODES_OPTION_META_KEYS } from '../fieldKeys';
import type { BareMetalNodesPaths } from '../types';

/** 计费项 composition 条目 */
interface BillDeviceCompositionItem {
  code?: string;
  vendor?: string;
  device?: string;
  quantity?: {
    total?: number;
    unit?: { type?: string; default?: string; supported?: string[] };
  };
  resourceType?: string;
}

/** 计费项 detail 解析结果 */
export interface BillDeviceDetailItem {
  architecture?: string;
  vendor?: string;
  device?: string;
  deviceID?: string;
  composition?: BillDeviceCompositionItem[];
}

/** 解析 VendorAndDeviceRadioList 返回的 detail JSON */
export function parseBillDeviceDetail(detail?: string): BillDeviceDetailItem | null {
  if (!detail) return null;
  try {
    const parsed = JSON.parse(detail) as unknown;
    return Array.isArray(parsed) ? (parsed[0] as BillDeviceDetailItem) : null;
  } catch {
    return null;
  }
}

/**
 * 内存数量展示：优先 composition / spec 解析出的单位，缺省 Gi（物理节点 API）。
 * 容量类可按 supported 升到更大单位；仅 UI。
 */
export function formatBareMetalMemoryText(totalMemory: unknown, unit?: unknown, supported?: unknown): string {
  if (totalMemory == null || totalMemory === '') return '';
  const memoryUnit = String(unit || '').trim() || 'Gi';
  return formatCapacityForDisplay(totalMemory, memoryUnit, supported) || `${totalMemory}${memoryUnit}`;
}

/** 从计费 detail 构造节点 spec，供预览/display 使用 */
export function buildNodeSpecFromBillDetail(detailItem: BillDeviceDetailItem | null): Record<string, unknown> {
  const composition = detailItem?.composition || [];
  const cpuItem = composition.find((item) => item.code === 'CPU');
  const memoryItem = composition.find((item) => item.code === 'Memory');
  const gpuItem = composition.find((item) => item.code === 'GPU');
  const memorySupported = memoryItem?.quantity?.unit?.supported;

  return {
    architecture: detailItem?.architecture,
    cpu: cpuItem
      ? {
          totalCores: cpuItem.quantity?.total,
          manufacturer: cpuItem.vendor,
          model: cpuItem.device,
        }
      : undefined,
    memory: memoryItem
      ? {
          totalMemory: memoryItem.quantity?.total,
          type: memoryItem.resourceType,
          /** 展示/预览用单位，来自 quantity.unit.default */
          unit: String(memoryItem.quantity?.unit?.default || '').trim() || 'Gi',
          ...(Array.isArray(memorySupported) && memorySupported.length ? { supportedUnits: memorySupported } : {}),
        }
      : undefined,
    gpus: gpuItem
      ? [
          {
            model: gpuItem.device || gpuItem.vendor,
            num: gpuItem.quantity?.total,
            vendor: gpuItem.vendor,
          },
        ]
      : undefined,
    manufacturer: detailItem?.vendor,
    model: detailItem?.device,
  };
}

/** 从计费 detail 提取 GPU 型号，写入隐藏字段 gpuName */
export function extractGpuNameFromBillDetail(detailItem: BillDeviceDetailItem | null): string | undefined {
  const gpuItem = detailItem?.composition?.find((item) => item.code === 'GPU');
  return gpuItem?.device || gpuItem?.vendor || undefined;
}

/** 组装计价模式 optionMeta.node 记录 */
export function buildBillingNodeOptionRecord(
  selection: VendorDeviceSelection,
  detailItem: BillDeviceDetailItem | null,
): Record<string, unknown> {
  const nodeKey = selection.billItemId || BARE_METAL_NODES_OPTION_META_KEYS.node;
  const displayLabel = formatVendorDeviceLabel({ vendor: selection.vendor, device: selection.device });
  return {
    value: nodeKey,
    name: displayLabel,
    label: displayLabel,
    vendor: selection.vendor,
    device: selection.device,
    vendorID: selection.vendorID,
    deviceID: selection.deviceID,
    deviceType: selection.deviceType,
    billItemId: selection.billItemId,
    architecture: detailItem?.architecture,
    detail: selection.detail,
    spec: buildNodeSpecFromBillDetail(detailItem),
    /** 会话内保留，供列表回填匹配；不进入提交 API */
    useSubResourcePricing: Boolean(selection.useSubResourcePricing),
    ...(selection.quantityUnit ? { quantityUnit: selection.quantityUnit } : {}),
  };
}

/** 计价模式：写入 optionMeta，清空 bmsNode（物理节点名专用） */
export function applyBillingNodeSelection(
  form: FormInstance,
  paths: BareMetalNodesPaths,
  selection: VendorDeviceSelection,
): string | undefined {
  const detailItem = parseBillDeviceDetail(selection.detail);
  const gpuName = extractGpuNameFromBillDetail(detailItem);

  form.setFieldsValue({
    [paths.bmsNode]: undefined,
    [paths.gpuName]: gpuName,
    /** 计价选规格不绑定物理节点，清掉节点带回的静态 IP */
    staticIpv4: undefined,
    _staticIpv4FromNode: false,
  });

  const nodeKey = selection.billItemId || BARE_METAL_NODES_OPTION_META_KEYS.node;
  const built = buildOptionFromRaw(nodeKey, buildBillingNodeOptionRecord(selection, detailItem));
  syncOptionMetaFromRaw(form, nodeKey, built, BARE_METAL_NODES_OPTION_META_KEYS.node);

  return gpuName;
}

/** 计价模式：清除节点选择与 optionMeta */
export function clearBillingNodeSelection(form: FormInstance, paths: BareMetalNodesPaths): void {
  form.setFieldsValue({
    [paths.bmsNode]: undefined,
    [paths.gpuName]: undefined,
    staticIpv4: undefined,
    _staticIpv4FromNode: false,
  });
  syncOptionMetaFromRaw(form, undefined, undefined, BARE_METAL_NODES_OPTION_META_KEYS.node);
}
